import {IGPS, IMapable, ITrip} from '@fotolog/data'
import {Entity, createResource} from '@data-client/rest'
import {RestEndpoint, RestGenerics} from '@data-client/rest'
import {immerable} from 'immer'
import {findIndex} from 'lodash'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class AppEndpoint<O extends RestGenerics = any> extends RestEndpoint<O> {
  override urlPrefix = '/api'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override async getRequestInit(body: any): Promise<RequestInit> {
    return {
      ...(await super.getRequestInit(body)),
      credentials: 'include',
    }
  }

  // override errorPolicy(): 'soft' | undefined {
  //   return 'soft'
  // }

  override getHeaders(headers: HeadersInit): HeadersInit {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // const session = useSession()
    const sessionId = localStorage.getItem('sessionId') || '0'
    return {
      ...headers,
      'App-User': `${sessionId}`, // helps with trouble-shooting
    }
  }
}

class AppEntity extends Entity {
  readonly id: number = 0

  pk() {
    return this.id.toString()
  }
}

// @ts-expect-error: see https://github.com/microsoft/TypeScript/issues/42830#issuecomment-780714433
AppEntity[immerable] = true // https://immerjs.github.io/immer/complex-objects/

export class Team extends AppEntity {
  userId = 0
  name = ''
  domain = ''
  logo = ''
  theme = {
    primary: 'green',
  }
}

export const TeamResource = createResource({
  Endpoint: AppEndpoint,
  path: '/team/:id',
  schema: Team,
})

export class User extends AppEntity {
  teamId = 0
  team: Team = Team.fromJS()
  name = ''
  email = ''
  avatar = ''
  search = ''
  trips: Trip[] = []
  created = new Date(0)
  lastActive = new Date(0)
  // constructor() {
  //   super()
  //   this.email = email
  // }
}

export class Scope extends User {
  tags: string[] = []
  isTeamLead = false
}

export const SignUpResource = createResource({
  Endpoint: AppEndpoint,
  path: '/signup/:id',
  schema: User,
})

export const UserResource = createResource({
  Endpoint: AppEndpoint,
  path: '/account/:id',
  schema: User,
  // static signIn() {
  //   const endpoint = this.create()
  //   return endpoint.extend({
  //     url: () => `${AppResource.urlRoot}/signin`,
  //   })
  // }

  // static resetPassword() {
  //   const endpoint = this.create()
  //   return endpoint.extend({
  //     url: () => `${this.urlRoot}/reset-password`,
  //   })
  // }
})

export class SignIn extends AppEntity {
  name = ''
  email = ''
  password = ''
  token = ''
}

export const SignInResource = createResource({
  Endpoint: AppEndpoint,
  path: '/signin/:id',
  schema: SignIn,
})

export const MemberResource = createResource({
  Endpoint: AppEndpoint,
  path: '/team-member/:id',
  schema: User,
})

export class UserScope extends AppEntity {
  name = ''
  email = ''
  avatar = ''
  search = ''
}

export const UserScopeResource = createResource({
  Endpoint: AppEndpoint,
  path: '/user-scope/:id',
  schema: UserScope,
})

export class Email extends AppEntity {
  subject = ''
  body = ''
  level = 0
  read = false
  created = new Date(0)
}

export const EmailResource = createResource({
  Endpoint: AppEndpoint,
  path: '/email/:id',
  schema: Email,
})

// TODO: handling circular refs:
// - https://resthooks.io/rest/guides/relational-data#circular-dependencies

export class Trip extends AppEntity {
  userId = 0
  user?: Pick<User, 'name' | 'avatar'> = User.fromJS()
  title = ''
  report = ''
  cover?: Foto = Foto.fromJS() // TODO: not getting instantiated as a Foto()
  coverId? = 0
  // prior?: Trip = Trip.fromJS() // TODO: causing stack error
  priorId? = 0
  // parent?: Trip = Trip.fromJS() // TODO: causing stack error
  parentId? = 0
  nextId? = 0
  tags: string[] = []
  fotos: Foto[] = []
  children: Trip[] = []
  public = false
  route: number[][] = []
  happened = new Date(0)
  created = new Date(0)

  get slug() {
    return this.title.replace(/[^\w\s]+/gu, '').replace(/\s+/gu, '-')
  }

  get mapables() {
    const l: IMapable[] = []
    for (const foto of this.fotos) {
      if (foto.gps) {
        l.push({
          foto,
          trip: this as ITrip,
          id: foto.id,
          title: foto.caption,
          img: foto.thumbnail,
          gps: foto.gps,
          marker: true,
          clicked: 0,
        })
      }
    }
    for (const child of this.children) {
      const foto = child.cover
      if (foto?.gps) {
        l.push({
          foto,
          trip: child as ITrip,
          id: foto.id,
          title: child.title,
          img: foto.thumbnail,
          gps: foto.gps,
          marker: true,
          clicked: 0,
        })
      }
    }
    return l
  }

  cards() {
    const l = [...this.fotos]
    for (const trip of this.children) {
      if (trip.cover) {
        // make sub-trip look like a foto
        l.push(
          Foto.fromJS({...trip.cover, caption: trip.title, story: trip.report, took: trip.happened, isCover: true})
        )
      }
    }
    l.sort((a, b) => (a.took > b.took ? 1 : -1))
    return l
  }

  lightbox(foto: Foto) {
    // NOTE: assuming fotos were rendered using Trip.cards() ordering
    const idx = findIndex(this.cards(), f => f.id === foto.id)
    if (idx !== -1) {
      window.app.lightbox?.openGallery(idx)
    }
  }
}

export interface TripsFilter {
  scope: number
  tags?: string[]
  start?: number
  end?: number
}

const TripResourceBase = createResource({
  Endpoint: AppEndpoint,
  path: '/trip/:id',
  schema: Trip,
})
export const TripResource = {
  ...TripResourceBase,
  // create: TripResourceBase.create.extend({
  //   searchParams: {} as {scope?: number},
  // }),
  getList: TripResourceBase.getList.extend({
    searchParams: {} as TripsFilter,
  }),
  // TODO: a way to support different shapes for different actions?
  // - could remove coverId from Trip class since that was only added to support the update case
  // update: TripResourceBase.getList.extend({
  //   body: {} as Trip & {coverId?: number},
  // }),
}

export const exportTrip = new AppEndpoint({
  method: 'POST',
  path: '/trip/:id/export',
})

export class Foto extends AppEntity {
  tripId = 0
  // TODO: figure out how to define recursive relationships like this
  // trip?: Trip = Trip.fromJS()
  caption = ''
  story = ''
  urls: {
    orig: string
    optim?: string
    thumb?: string
  } = {orig: '/wut.jpg'}
  rating: number | null | undefined = 0
  tags: string[] = []
  gps: IGPS = {lat: 48, lng: -122}
  took = new Date(0)
  isCover = false // TODO: a kludge so that TripFotos can do the needful

  get url() {
    if (window.screen.availWidth < 500) {
      return this.thumbnail
    }
    return this.urls.optim || this.urls.orig || '/assets/img/default-cover.png'
  }

  get thumbnail() {
    return this.urls.thumb || this.urls.orig || '/assets/img/default-cover.png'
  }

  get slug() {
    return this.caption.replace(/[^\w\s]+/gu, '').replace(/\s+/gu, '-')
  }

  // get isCover() {
  //   return this.id === this.trip?.coverId
  // }
}

export const FotoResource = createResource({
  Endpoint: AppEndpoint,
  path: '/foto/:id',
  schema: Foto,
})

export class Acl extends AppEntity {
  userId = 0
  name = ''
  team = false
  token: string | null = ''
  tripIds: number[] = []
  incUserIds: number[] = []
  excUserIds: number[] = []
  created = new Date(0)
}

export const AclResource = createResource({
  Endpoint: AppEndpoint,
  path: '/acl/:id',
  schema: Acl,
})

// export class FileUpload extends AppResource {
//   static updateLogo() {
//     const endpoint = this.update()
//     return endpoint.extend({
//       url: ({ id }) => `${this.urlRoot}/teams/${id}/logo`,
//     })
//   }
// }

Foto.schema = {
  took: Date,
}

Trip.schema = {
  user: User,
  cover: Foto,
  fotos: [Foto],
  children: [Trip],
  happened: Date,
}

const PublicTripResourceBase = createResource({
  Endpoint: AppEndpoint,
  path: '/public/trip/:id',
  schema: Trip,
})
export const PublicTripResource = {
  ...PublicTripResourceBase,
  get: PublicTripResourceBase.get.extend({
    searchParams: {} as {token?: string | null} | undefined,
  }),
  getList: PublicTripResourceBase.getList.extend({
    searchParams: {} as TripsFilter,
  }),
}
